import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { userProfileService } from "@/services/userProfileService";
import { MAX_SOURCE_OF_TRUTH_DOCS } from "@/lib/sourceOfTruthPreferences";
import { SOURCE_OF_TRUTH_QUERY_KEY } from "@/hooks/useSourceOfTruth";
import {
  removeDocumentFromSourceOfTruth,
  toggleDocumentSourceOfTruth,
} from "@/services/sourceOfTruthService";

export function SourceOfTruthManager() {
  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: docIds = [], isLoading } = useQuery({
    queryKey: SOURCE_OF_TRUTH_QUERY_KEY,
    queryFn: () => userProfileService.getSourceOfTruthDocIds(),
  });

  const { data: allDocs = [] } = useQuery({
    queryKey: ["/api/knowledge-base-items", "ssot-picker"],
    queryFn: () => knowledgeBaseService.getKnowledgeBaseItems(),
    enabled: pickerOpen,
  });

  const { data: canonDocs = [] } = useQuery({
    queryKey: [...SOURCE_OF_TRUTH_QUERY_KEY, "docs", docIds.join(",")],
    queryFn: async () => {
      const items = await Promise.all(
        docIds.map((id) => knowledgeBaseService.getKnowledgeBaseItem(id).catch(() => null))
      );
      return items.filter(Boolean) as Awaited<ReturnType<typeof knowledgeBaseService.getKnowledgeBaseItem>>[];
    },
    enabled: docIds.length > 0,
  });

  const availableToAdd = useMemo(
    () => allDocs.filter((d) => !docIds.includes(d.id)),
    [allDocs, docIds]
  );

  const estimatedChars = useMemo(
    () => canonDocs.reduce((sum, d) => sum + String(d.content ?? "").length, 0),
    [canonDocs]
  );

  const handleRemove = async (id: string) => {
    try {
      await removeDocumentFromSourceOfTruth(id);
      toast({ title: "Removed from Source of Truth" });
    } catch (e) {
      toast({
        title: "Could not update",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleAdd = async (id: string) => {
    try {
      if (docIds.length >= MAX_SOURCE_OF_TRUTH_DOCS) {
        toast({
          title: "Limit reached",
          description: `You can designate at most ${MAX_SOURCE_OF_TRUTH_DOCS} canon documents.`,
          variant: "destructive",
        });
        return;
      }
      await toggleDocumentSourceOfTruth(id);
      toast({ title: "Added to Source of Truth" });
      setPickerOpen(false);
    } catch (e) {
      toast({
        title: "Could not add",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 rounded-lg border p-4" data-testid="section-source-of-truth">
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-1">
          <h4 className="font-medium">Source of Truth</h4>
          <p className="text-sm text-muted-foreground">
            These documents are always included in every chat as your authoritative canon—alongside
            personalization, RAG, and topic context. Use for principles, playbooks, and worldview docs.
          </p>
        </div>
      </div>

      {estimatedChars > 24_000 && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          Canon content is large (~{Math.round(estimatedChars / 4)} tokens). Older sections may be truncated per message.
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : canonDocs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No canon documents yet.</p>
      ) : (
        <ul className="space-y-2">
          {canonDocs.map((doc, index) => (
            <li
              key={doc.id}
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              data-testid={`ssot-doc-${doc.id}`}
            >
              <span className="truncate">
                <Badge variant="outline" className="mr-2 text-[10px] h-5">
                  {index + 1}
                </Badge>
                {doc.title || "Untitled"}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => handleRemove(doc.id)}
                aria-label={`Remove ${doc.title} from Source of Truth`}
                data-testid={`button-remove-ssot-${doc.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPickerOpen(true)}
          disabled={docIds.length >= MAX_SOURCE_OF_TRUTH_DOCS}
          data-testid="button-add-ssot-doc"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add document
        </Button>
        <span className="text-xs text-muted-foreground">
          {docIds.length}/{MAX_SOURCE_OF_TRUTH_DOCS}
        </span>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md p-0">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>Add Source of Truth document</DialogTitle>
          </DialogHeader>
          <Command>
            <CommandInput placeholder="Search library…" />
            <CommandList>
              <CommandEmpty>No documents found.</CommandEmpty>
              <CommandGroup>
                {availableToAdd.map((doc) => (
                  <CommandItem
                    key={doc.id}
                    value={`${doc.title} ${doc.id}`}
                    onSelect={() => handleAdd(doc.id)}
                  >
                    {doc.title || "Untitled"}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  );
}
